import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import styled from 'styled-components';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { RegisterCredentials } from '../../types';

// Validation schema
const registerSchema = yup.object().shape({
  username: yup
    .string()
    .min(3, 'Nome de usuário deve ter pelo menos 3 caracteres')
    .max(20, 'Nome de usuário deve ter menos de 20 caracteres')
    .matches(/^[a-zA-Z0-9_]+$/, 'Nome de usuário pode conter apenas letras, números e sublinhados')
    .required('Nome de usuário é obrigatório'),
  email: yup
    .string()
    .email('Por favor, insira um endereço de email válido')
    .required('Email é obrigatório'),
  password: yup
    .string()
    .min(6, 'Senha deve ter pelo menos 6 caracteres')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
      'Senha deve conter pelo menos uma letra maiúscula, uma minúscula e um número'
    )
    .required('Senha é obrigatória'),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref('password')], 'Senhas devem coincidir')
    .required('Por favor, confirme sua senha'),
});

// Reuse styled components from Login with minor modifications
const RegisterContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 2rem;
`;

const RegisterCard = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  padding: 3rem;
  width: 100%;
  max-width: 450px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #667eea, #764ba2);
  }
`;

const Title = styled.h2`
  color: #333;
  text-align: center;
  margin-bottom: 0.5rem;
  font-size: 2rem;
  font-weight: 600;
`;

const Subtitle = styled.p`
  color: #666;
  text-align: center;
  margin-bottom: 2rem;
  font-size: 0.9rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 500;
  color: #333;
  font-size: 0.9rem;
`;

const Input = styled.input<{ hasError?: boolean }>`
  padding: 0.75rem;
  border: 2px solid ${props => props.hasError ? '#ff6b6b' : '#e1e5e9'};
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.2s ease;
  background: #fafafa;

  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? '#ff6b6b' : '#667eea'};
    background: white;
  }

  &::placeholder {
    color: #999;
  }
`;

const ErrorMessage = styled.span`
  color: #ff6b6b;
  font-size: 0.8rem;
  margin-top: 0.25rem;
`;

const Button = styled.button<{ isLoading?: boolean }>`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 0.875rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: ${props => props.isLoading ? 'not-allowed' : 'pointer'};
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  opacity: ${props => props.isLoading ? 0.7 : 1};

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(102, 126, 234, 0.3);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid transparent;
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-right: 8px;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const Divider = styled.div`
  text-align: center;
  margin: 1.5rem 0;
  position: relative;
  color: #999;
  font-size: 0.9rem;

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    height: 1px;
    background: #e1e5e9;
    z-index: 1;
  }

  span {
    background: white;
    padding: 0 1rem;
    position: relative;
    z-index: 2;
  }
`;

const LoginLink = styled.div`
  text-align: center;
  margin-top: 1rem;
  color: #666;
  font-size: 0.9rem;

  a {
    color: #667eea;
    text-decoration: none;
    font-weight: 500;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const AlertMessage = styled.div<{ type: 'success' | 'error' }>`
  padding: 0.75rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  font-size: 0.9rem;
  background: ${props => props.type === 'error' ? '#ffeaea' : '#eafff0'};
  border: 1px solid ${props => props.type === 'error' ? '#ffcbcb' : '#c3f0d1'};
  color: ${props => props.type === 'error' ? '#d63031' : '#00b894'};
`;

const PasswordStrength = styled.div<{ strength: number }>`
  height: 4px;
  width: 100%;
  background: #e1e5e9;
  border-radius: 2px;
  margin-top: 0.5rem;
  overflow: hidden;

  &::after {
    content: '';
    display: block;
    height: 100%;
    width: ${props => (props.strength / 4) * 100}%;
    background: ${props => {
      if (props.strength <= 1) return '#ff6b6b';
      if (props.strength <= 2) return '#ffa500';
      if (props.strength <= 3) return '#ffeb3b';
      return '#4caf50';
    }};
    transition: width 0.3s ease, background-color 0.3s ease;
  }
`;

const PasswordHint = styled.div`
  font-size: 0.75rem;
  color: #666;
  margin-top: 0.25rem;
`;

const Register: React.FC = () => {
  const [apiError, setApiError] = useState<string>('');
  const [passwordStrength, setPasswordStrength] = useState<number>(0);
  const { register: registerUser, state } = useAuth();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<RegisterCredentials>({
    resolver: yupResolver(registerSchema),
  });

  const watchPassword = watch('password', '');

  // Calculate password strength
  React.useEffect(() => {
    const calculateStrength = (password: string): number => {
      let strength = 0;
      if (password.length >= 6) strength++;
      if (/[a-z]/.test(password)) strength++;
      if (/[A-Z]/.test(password)) strength++;
      if (/\d/.test(password)) strength++;
      if (/[^a-zA-Z0-9]/.test(password)) strength++;
      return Math.min(strength, 4);
    };

    setPasswordStrength(calculateStrength(watchPassword));
  }, [watchPassword]);

  const getPasswordStrengthText = (strength: number): string => {
    switch (strength) {
      case 0:
      case 1:
        return 'Fraca';
      case 2:
        return 'Razoável';
      case 3:
        return 'Boa';
      case 4:
        return 'Forte';
      default:
        return '';
    }
  };

  const onSubmit = async (data: RegisterCredentials) => {
    try {
      setApiError('');
      await registerUser(data);
      navigate('/chat');
    } catch (error: any) {
      setApiError(error.message || 'Falha no registro. Tente novamente.');
    }
  };

  return (
    <RegisterContainer>
      <RegisterCard>
        <Title>Criar Conta</Title>
        <Subtitle>Junte-se a nós e comece a conversar</Subtitle>

        {apiError && (
          <AlertMessage type="error">
            {apiError}
          </AlertMessage>
        )}

        <Form onSubmit={handleSubmit(onSubmit)}>
          <FormGroup>
            <Label htmlFor="username">Nome de Usuário</Label>
            <Input
              {...register('username')}
              type="text"
              id="username"
              placeholder="Escolha um nome de usuário"
              hasError={!!errors.username}
              autoComplete="username"
            />
            {errors.username && (
              <ErrorMessage>{errors.username.message}</ErrorMessage>
            )}
          </FormGroup>

          <FormGroup>
            <Label htmlFor="email">Endereço de Email</Label>
            <Input
              {...register('email')}
              type="email"
              id="email"
              placeholder="Digite seu email"
              hasError={!!errors.email}
              autoComplete="email"
            />
            {errors.email && (
              <ErrorMessage>{errors.email.message}</ErrorMessage>
            )}
          </FormGroup>

          <FormGroup>
            <Label htmlFor="password">Senha</Label>
            <Input
              {...register('password')}
              type="password"
              id="password"
              placeholder="Crie uma senha"
              hasError={!!errors.password}
              autoComplete="new-password"
            />
            {watchPassword && (
              <PasswordStrength strength={passwordStrength} />
            )}
            {watchPassword && (
              <PasswordHint>
                Força da senha: {getPasswordStrengthText(passwordStrength)}
              </PasswordHint>
            )}
            {errors.password && (
              <ErrorMessage>{errors.password.message}</ErrorMessage>
            )}
          </FormGroup>

          <FormGroup>
            <Label htmlFor="confirmPassword">Confirmar Senha</Label>
            <Input
              {...register('confirmPassword')}
              type="password"
              id="confirmPassword"
              placeholder="Confirme sua senha"
              hasError={!!errors.confirmPassword}
              autoComplete="new-password"
            />
            {errors.confirmPassword && (
              <ErrorMessage>{errors.confirmPassword.message}</ErrorMessage>
            )}
          </FormGroup>

          <Button 
            type="submit" 
            disabled={state.isLoading}
            isLoading={state.isLoading}
          >
            {state.isLoading && <LoadingSpinner />}
            {state.isLoading ? 'Criando Conta...' : 'Criar Conta'}
          </Button>
        </Form>

        <Divider>
          <span>ou</span>
        </Divider>

        <LoginLink>
          Já tem uma conta?{' '}
          <Link to="/login">Entre aqui</Link>
        </LoginLink>
      </RegisterCard>
    </RegisterContainer>
  );
};

export default Register; 